/**
 * Client Management React Query Hooks
 * Custom hooks for client data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiCrmApi } from '../api/aiCrmApi';
import type {
  ClientFilters,
  CreateClientInput,
  UpdateClientInput,
  CreateOrderInput,
  UpdateOrderInput,
  OrderFilters
} from '../types/client';

// ===== QUERY KEYS =====

export const CLIENT_KEYS = {
  all: ['clients'] as const,
  lists: () => [...CLIENT_KEYS.all, 'list'] as const,
  list: (filters?: ClientFilters) => [...CLIENT_KEYS.lists(), filters] as const,
  details: () => [...CLIENT_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...CLIENT_KEYS.details(), id] as const,
  analytics: (id: string) => [...CLIENT_KEYS.all, 'analytics', id] as const,
  atRisk: (threshold?: number) => [...CLIENT_KEYS.all, 'at-risk', threshold] as const,
  upsell: (threshold?: number) => [...CLIENT_KEYS.all, 'upsell', threshold] as const,
  orders: (clientId: string, filters?: OrderFilters) => [...CLIENT_KEYS.all, clientId, 'orders', filters] as const,
};

// ===== CLIENT QUERIES =====

/**
 * Hook to fetch clients with filters
 */
export const useClients = (filters?: ClientFilters) => {
  return useQuery({
    queryKey: CLIENT_KEYS.list(filters),
    queryFn: () => aiCrmApi.clients.getClients(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch single client
 */
export const useClient = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: CLIENT_KEYS.detail(id),
    queryFn: () => aiCrmApi.clients.getClientById(id),
    enabled: options?.enabled !== undefined ? options.enabled && !!id : !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch client analytics
 */
export const useClientAnalytics = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: CLIENT_KEYS.analytics(id),
    queryFn: () => aiCrmApi.clients.getClientAnalytics(id),
    enabled: options?.enabled !== undefined ? options.enabled && !!id : !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch at-risk clients
 */
export const useAtRiskClients = (riskThreshold = 70, limit = 20) => {
  return useQuery({
    queryKey: CLIENT_KEYS.atRisk(riskThreshold),
    queryFn: () => aiCrmApi.clients.getAtRiskClients(riskThreshold, limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch upsell opportunities
 */
export const useUpsellOpportunities = (potentialThreshold = 70, limit = 20) => {
  return useQuery({
    queryKey: CLIENT_KEYS.upsell(potentialThreshold),
    queryFn: () => aiCrmApi.clients.getUpsellOpportunities(potentialThreshold, limit),
    staleTime: 10 * 60 * 1000,
  });
};

// ===== CLIENT MUTATIONS =====

/**
 * Hook to create new client
 */
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClientInput) => aiCrmApi.clients.createClient(data),
    onSuccess: () => {
      // Invalidate client lists to show new client
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
    },
  });
};

/**
 * Hook to update client
 */
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientInput }) =>
      aiCrmApi.clients.updateClient(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific client and list
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.analytics(variables.id) });
    },
  });
};

/**
 * Hook to delete client
 */
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiCrmApi.clients.deleteClient(id),
    onSuccess: () => {
      // Invalidate client lists
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.atRisk() });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.upsell() });
    },
  });
};

/**
 * Hook to update client status
 */
export const useUpdateClientStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      aiCrmApi.clients.updateClientStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.atRisk() });
    },
  });
};

/**
 * Hook to update client tier
 */
export const useUpdateClientTier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, tier }: { id: string; tier: string }) =>
      aiCrmApi.clients.updateClientTier(id, tier),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
    },
  });
};

/**
 * Hook to assign staff to client
 */
export const useAssignClientStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assignedToUserId }: { id: string; assignedToUserId: string | null }) =>
      aiCrmApi.clients.assignClientStaff(id, assignedToUserId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.lists() });
    },
  });
};

// ===== ORDER QUERIES =====

/**
 * Hook to fetch client orders
 */
export const useClientOrders = (clientId: string, filters?: OrderFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: CLIENT_KEYS.orders(clientId, filters),
    queryFn: () => aiCrmApi.orders.getClientOrders(clientId, filters),
    enabled: options?.enabled !== undefined ? options.enabled && !!clientId : !!clientId,
    staleTime: 2 * 60 * 1000,
  });
};

// ===== ORDER MUTATIONS =====

/**
 * Hook to create order
 */
export const useCreateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: CreateOrderInput }) =>
      aiCrmApi.orders.createOrder(clientId, data),
    onSuccess: (_, variables) => {
      // Invalidate client orders and details
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.orders(variables.clientId) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.detail(variables.clientId) });
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.analytics(variables.clientId) });
    },
  });
};

/**
 * Hook to update order
 */
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderInput }) =>
      aiCrmApi.orders.updateOrder(id, data),
    onSuccess: () => {
      // Invalidate all order queries (we don't know which client)
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.all });
    },
  });
};

/**
 * Hook to delete order
 */
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => aiCrmApi.orders.deleteOrder(id),
    onSuccess: () => {
      // Invalidate all order queries
      queryClient.invalidateQueries({ queryKey: CLIENT_KEYS.all });
    },
  });
};
